import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type FidesChatPanelMode = 'chat' | 'transcript';

export interface FidesChatContextValue {
  openConversationId: string | null;
  panelMode: FidesChatPanelMode;
  isPanelOpen: boolean;
  isChatMinimized: boolean;
  openConversation: (conversationId: string) => void;
  openTranscript: (conversationId: string) => void;
  closePanel: () => void;
  minimizePanel: () => void;
  restorePanel: () => void;
}

const FidesChatContext = createContext<FidesChatContextValue | null>(null);

function stripChatQueryParams(search: string): string {
  const params = new URLSearchParams(search);
  if (!params.has('chat') && !params.has('chatId')) {
    return search;
  }
  params.delete('chat');
  params.delete('chatId');
  const next = params.toString();
  return next ? `?${next}` : '';
}

export const FidesChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openConversationId, setOpenConversationId] = useState<string | null>(null);
  const [panelMode, setPanelMode] = useState<FidesChatPanelMode>('chat');
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isChatMinimized, setIsChatMinimized] = useState(false);

  const openConversation = useCallback((conversationId: string) => {
    setOpenConversationId(conversationId);
    setPanelMode('chat');
    setIsPanelOpen(true);
    setIsChatMinimized(false);
  }, []);

  const openTranscript = useCallback((conversationId: string) => {
    setOpenConversationId(conversationId);
    setPanelMode('transcript');
    setIsPanelOpen(true);
    setIsChatMinimized(false);
  }, []);

  const closePanel = useCallback(() => {
    setOpenConversationId(null);
    setPanelMode('chat');
    setIsPanelOpen(false);
    setIsChatMinimized(false);

    const nextSearch = stripChatQueryParams(location.search);
    if (nextSearch !== location.search) {
      navigate({ pathname: location.pathname, search: nextSearch }, { replace: true });
    }
  }, [location.pathname, location.search, navigate]);

  const minimizePanel = useCallback(() => {
    setIsChatMinimized(true);
  }, []);

  const restorePanel = useCallback(() => {
    setIsChatMinimized(false);
  }, []);

  const value = useMemo(
    () => ({
      openConversationId,
      panelMode,
      isPanelOpen,
      isChatMinimized,
      openConversation,
      openTranscript,
      closePanel,
      minimizePanel,
      restorePanel,
    }),
    [
      closePanel,
      isChatMinimized,
      isPanelOpen,
      minimizePanel,
      openConversation,
      openConversationId,
      openTranscript,
      panelMode,
      restorePanel,
    ],
  );

  return <FidesChatContext.Provider value={value}>{children}</FidesChatContext.Provider>;
};

export function useFidesChat() {
  return useContext(FidesChatContext);
}
